import { useMemo } from 'react'
import qrcode from 'qrcode-generator'

export interface QrCodeProps {
  /** The payload. For MFA enrolment this is the otpauth:// URI. */
  value: string
  /** Rendered size. A QR below ~10rem is unreliable for phone cameras. */
  size?: string
  /**
   * What a screen reader announces. Never the raw value — reading an
   * otpauth URI character by character is not usable, and it would speak the
   * shared secret aloud. Describe the code and put the secret in a control
   * the user can actually copy.
   */
  label: string
}

/**
 * QrCode — an inline SVG QR code.
 *
 * SVG rather than canvas so it stays sharp at any zoom, survives printing,
 * and inherits theme colours. Canvas would also be invisible to forced-colors
 * mode, where the whole point is that the OS gets to repaint the UI.
 *
 * The quiet zone is not decoration: the spec requires four modules of margin,
 * and scanners genuinely fail without it — which is the kind of bug that
 * looks like "the camera is bad" rather than "the QR is wrong".
 */
export function QrCode({ value, size = '11rem', label }: QrCodeProps) {
  const { path, dimension } = useMemo(() => {
    // Type 0 picks the smallest version that fits. Error correction M is the
    // usual choice for screen display: L saves a few modules but stops
    // tolerating the glare and moiré of a phone photographing a monitor.
    const qr = qrcode(0, 'M')
    qr.addData(value)
    qr.make()

    const count = qr.getModuleCount()
    const margin = 4
    const segments: string[] = []

    // One path for every dark module, which keeps the DOM to a single node
    // instead of ~1500 rects.
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) {
          segments.push(`M${col + margin} ${row + margin}h1v1h-1z`)
        }
      }
    }
    return { path: segments.join(''), dimension: count + margin * 2 }
  }, [value])

  // shapeRendering="crispEdges": without it browsers antialias the module
  // edges, and the seams between adjacent squares show as faint grid lines
  // that some scanners misread as module boundaries.
  return (
    <svg
      className="lg-qr"
      viewBox={`0 0 ${dimension} ${dimension}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      shapeRendering="crispEdges"
    >
      {/* An explicit light background. A transparent QR inverts in dark mode,
          and an inverted QR is unreadable to most scanners. */}
      <rect width={dimension} height={dimension} className="lg-qr__quiet" />
      <path d={path} className="lg-qr__module" />
    </svg>
  )
}
